import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import {
  getAuth,
  onIdTokenChanged, // Changed from onAuthStateChanged
  signOut as firebaseSignOut,
} from "firebase/auth";
import { User } from "../types/index.js";
import { User as FirebaseUser } from "firebase/auth";
import { useToast } from "../hooks/use-toast.js";

// Type to represent the raw user data from the server before processing
type UserFromServer = {
  id: string;
  email: string;
  name: string | null;
  plan: "HOBBY" | "STANDARD" | "GROWTH";
  lastFreeRequestTimestamp: string | null;
  proRequestsThisMonth: { monthYear: string; count: number } | null;
  currentStreak: number;
  lastCheckinDate: string | null;
  capitalPoints: number;
  pitchDeckUrl: string | null;
};

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signOut: () => Promise<void>;
  refreshUser: () => Promise<Omit<User, 'token'> | undefined>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  // Helper to fetch and process user data from the server
  const fetchUserData = async (firebaseUser: FirebaseUser, retryCount = 0) => {
    const MAX_RETRIES = 2;
    const RETRY_DELAY_MS = 1000; // 1 second

    const idToken = await firebaseUser.getIdToken(true); // Force refresh
    const response = await fetch("/api/user/sync", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({
        id: firebaseUser.uid,
        email: firebaseUser.email,
        name: firebaseUser.displayName,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      const errorMessage = errorData.error || "Failed to sync user with backend.";

      if (errorMessage.includes("Token used too early") && retryCount < MAX_RETRIES) {
        console.warn(`Token used too early, retrying in ${RETRY_DELAY_MS}ms (Attempt ${retryCount + 1}/${MAX_RETRIES})`);
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY_MS));
        return fetchUserData(firebaseUser, retryCount + 1);
      }

      throw new Error(errorMessage);
    }

    return response.json();
  };

  // Helper to process user data from the server, converting server types to client types.
  const processUserFromServer = (
    userFromServer: UserFromServer
  ): Omit<User, "token"> => {
    return {
      ...userFromServer,
      // Convert name from `string | null` to `string` to match the frontend User type.
      name: userFromServer.name || "",
      // Convert plan from "FREE" to "free"
      plan: userFromServer.plan as "HOBBY" | "STANDARD" | "GROWTH",
      // Convert timestamp string to a number (milliseconds) for date calculations.
      lastFreeRequestTimestamp: userFromServer.lastFreeRequestTimestamp
        ? new Date(userFromServer.lastFreeRequestTimestamp).getTime()
        : undefined,
      // Convert `null` to `undefined` to match the frontend User type.
      proRequestsThisMonth: userFromServer.proRequestsThisMonth || undefined,
      currentStreak: userFromServer.currentStreak,
      lastCheckinDate: userFromServer.lastCheckinDate || undefined,
      capitalPoints: userFromServer.capitalPoints || 0,
      pitchDeckUrl: userFromServer.pitchDeckUrl || undefined,
    };
  };

  // Method to refresh user data from the server
  const refreshUser = async (): Promise<Omit<User, 'token'> | undefined> => {
    const auth = getAuth();
    const firebaseUser = auth.currentUser;
    if (!firebaseUser) return undefined;

    try {
      const dbUserFromServer = await fetchUserData(firebaseUser);
      const idToken = await firebaseUser.getIdToken(true); // Force refresh
      const processedUser = processUserFromServer(dbUserFromServer);
      setUser({ ...processedUser, token: idToken });
      return processedUser;
    } catch (error) {
      console.error("Error refreshing user:", error);
      throw error;
    }
  };

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onIdTokenChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          const dbUserFromServer = await fetchUserData(firebaseUser);
          const idToken = await firebaseUser.getIdToken();
          const processedUser = processUserFromServer(dbUserFromServer);
          setUser({ ...processedUser, token: idToken });
        } catch (error) {
          console.error("Error syncing user:", error);
          toast({
            title: "Authentication Error",
            description:
              "Could not sync your account. Please try logging in again.",
            variant: "destructive",
          });
          await firebaseSignOut(auth);
        } finally {
          setLoading(false);
        }
      } else {
        setUser(null);
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [toast]);

  const signOut = async () => {
    const auth = getAuth();
    await firebaseSignOut(auth);
    toast({
      title: "Logged out",
      description: "You've been successfully logged out.",
    });
  };

  return (
    <AuthContext.Provider value={{ user, loading, signOut, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
